import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import AmenityReservationStatusComponent from './AmenityReservationStatusComponent'
import {makeStyles} from '@mui/styles'
import {spaceMedium} from '../../assets/styles/spacing'
import {Grid, Typography} from '@mui/material'
import {useDispatch, useSelector} from 'react-redux'
import SimpleSpinner from '../SimpleSpinner'
import moment from 'moment'
import BookingPriceBreakdown from './BookingPriceBreakdown'
import SelectPaymentMethodsForResident from '../Services/SelectPaymentMethodsForResident'
import {updateAmenityReservationPaymentSettings} from '../../actions/amenitiesActions'
import MultilineTextInput from '../MultilineTextInput'
import Constants from './AmenitiesConstants'
import {fontSizeMediumLarge} from '../../assets/styles/typography'
import {rxrRedColor} from '../../assets/styles/color'
import {isReservationPastDue} from '../../Utils/amenityUtils'
import {SUPPORT_LINK} from '../../constants/AppConstants'

const useStyles = makeStyles({
  statusContainer: {
    marginBottom: spaceMedium,
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: `${spaceMedium}px 0`,
  },
  chargeWarningMessage: {
    fontSize: fontSizeMediumLarge,
    marginBottom: spaceMedium,
  },
})

function AmenityReservationChargeFlow(props) {
  const classes = useStyles()

  const dispatch = useDispatch()
  const [isSavingChanges, setIsSavingChanges] = useState(false)

  const reservation = useSelector(state => state.Amenities.amenityReservationsLookup[props.amenityReservationId])
  const [notes, setNotes] = useState(reservation.notes || '')

  useEffect(() => {
    if (!reservation) {
      return
    }

    setNotes(reservation.notes || '')
  }, [reservation])

  const handleChangePaymentMethod = paymentMethodId => {
    setIsSavingChanges(true)
    updateAmenityReservationPaymentSettings(dispatch, {
      id: reservation.id,
      paymentMethodId: paymentMethodId,
    })
      .catch(err => {
        console.error('Error updating amenity reservation payment settings', err)
      })
      .finally(() => {
        setIsSavingChanges(false)
      })
  }

  const handleChangeNotes = () => {
    setIsSavingChanges(true)
    updateAmenityReservationPaymentSettings(dispatch, {
      id: reservation.id,
      notes: notes,
    })
      .catch(err => {
        console.error('Error updating amenity reservation payment settings', err)
      })
      .finally(() => {
        setIsSavingChanges(false)
      })
  }

  return !reservation ? (
    <Grid item xs={12}>
      <div className={classes.spinnerContainer}>
        <SimpleSpinner />
      </div>
    </Grid>
  ) : (
    <React.Fragment>
      <div className={classes.statusContainer}>
        <AmenityReservationStatusComponent state={Constants.STATE_READY_TO_CHARGE} updatedAt={reservation.updatedAt} />
      </div>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {isReservationPastDue(reservation) ? (
            <Typography className={classes.chargeWarningMessage} style={{color: rxrRedColor}}>
              This reservation is past due. There may have been a system issue in charging it. Please try again manually or{' '}
              <a href={SUPPORT_LINK} rel="noreferrer" target="_blank">
                contact support
              </a>{' '}
              for assistance
            </Typography>
          ) : (
            <Typography className={classes.chargeWarningMessage}>
              This reservation will be automatically charged on {moment(reservation.startAt).add(7, 'days').format('MMM D, YYYY')}. Use this
              panel to make any necessary changes to the amount owed and to issue any refunds owed.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <SelectPaymentMethodsForResident
            residentId={reservation.residentId}
            onSelect={handleChangePaymentMethod}
            paymentMethodId={reservation.paymentMethodId}
          />
        </Grid>

        <Grid item xs={12}>
          <MultilineTextInput
            label="Notes"
            placeholder=""
            rows={4}
            rowsMax={4}
            isRequired={false}
            maxLength={2000}
            onChange={setNotes}
            value={notes}
            onBlur={handleChangeNotes}
          />
        </Grid>

        <Grid item xs={12}>
          <BookingPriceBreakdown
            amenityId={reservation.amenityId}
            residentId={reservation.residentId}
            id={reservation.id}
            startAt={reservation.startAt}
            endAt={reservation.endAt}
            hourlyCost={reservation.hourlyCost}
            depositCost={reservation.depositCost}
            depositPaymentId={reservation.depositPaymentId}
            bookingPaymentId={reservation.bookingPaymentId}
            readyForPayment={reservation.readyForPayment}
            ctasDisabled={isSavingChanges}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

AmenityReservationChargeFlow.propTypes = {
  amenityReservationId: PropTypes.string.isRequired,
}

export default AmenityReservationChargeFlow
